/**
 * 計助ダッシュボード用・レガシー生存曲線グラフ（ステップ曲線、論文用設定可能）
 */

import {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
  createContext,
  useContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Accordion, Button } from 'react-bootstrap';
import KeisukeChart from '../charts/KeisukeChart';
import KeisukeAxis from '../charts/KeisukeAxis';
import { createLinearScale, calculateNiceTicks, getColor } from '../charts/chartUtils';
import type { ChartDimensions, AxisScale } from '../charts/types';
import { copySvgElementAsPngToClipboard, normalizeSvgDimensionsForExport } from '../../../../utils/exportUtils';
import { KeisukeDashboardChartExportDropdown } from './KeisukeDashboardChartExportDropdown';
import styles from './KeisukeDashboardChart.module.scss';
import { DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX } from './keisukeDashboardBarChartSettings';
import { safeLocalStorageSetJson } from './keisukeDashboardLocalStorage';
import UTIF from 'utif';

const DEFAULT_MARGIN = { top: 40, right: 40, bottom: 60, left: 120 };

/** 群ごとの曲線設定 */
export interface SurvivalGroupSettings {
  lineColor: string;
  lineWidth: number;
  lineStyle: 'solid' | 'dashed' | 'dotted';
}

/** レガシー計助 SurvivalCurveView と同等のグラフ設定型 */
export interface SurvivalChartSettings {
  size: {
    width: number | null;
    height: number | null;
    plotAreaWidth: number | null;
    plotAreaHeight: number | null;
  };
  xAxis: { label: string; lineWidth: number; labelPosition: number };
  yAxis: { label: string; lineWidth: number; labelPosition: number };
  xAxisGrid: { show: boolean; lineStyle: 'solid' | 'dashed' | 'dotted'; opacity: number; color: string };
  yAxisGrid: { show: boolean; lineStyle: 'solid' | 'dashed' | 'dotted'; opacity: number; color: string };
  textStyles: {
    axisLabel: { fontSize: number; color: string };
    tickLabel: { fontSize: number; color: string };
    legendLabel: { fontSize: number; color: string };
  };
  legend: { show: boolean; position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' };
  defaultLineWidth: number;
  groups: Record<string, SurvivalGroupSettings>;
  /** グラフの背景色。'transparent' または CSS色 */
  chartBackgroundColor: string;
}

const STORAGE_KEY_SURVIVAL = 'keisuke_dashboard_survival_settings';
const STORAGE_KEY_SURVIVAL_LEGACY = 'keisuke_dashboard_legacy_survival_settings';
const DEFAULT_GROUP_COLORS = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#e83e8c', '#fd7e14'];

function createDefaultSettings(
  t: (key: string, opts?: { defaultValue?: string }) => string
): SurvivalChartSettings {
  return {
    size: { width: null, height: null, plotAreaWidth: null, plotAreaHeight: null },
    xAxis: { label: t('keisuke.survival.time'), lineWidth: 3, labelPosition: 40 },
    yAxis: { label: t('keisuke.survival.survivalRate'), lineWidth: 3, labelPosition: -65 },
    xAxisGrid: { show: false, lineStyle: 'dashed', opacity: 0.3, color: '#e5e5e5' },
    yAxisGrid: { show: true, lineStyle: 'dashed', opacity: 0.3, color: '#e5e5e5' },
    textStyles: {
      axisLabel: { fontSize: 16, color: '#333333' },
      tickLabel: { fontSize: 16, color: '#333333' },
      legendLabel: { fontSize: 12, color: '#333333' },
    },
    legend: { show: true, position: 'top-right' },
    defaultLineWidth: 2,
    groups: {},
    chartBackgroundColor: 'transparent',
  };
}

function loadSurvivalSettings(t: (key: string, opts?: { defaultValue?: string }) => string): SurvivalChartSettings {
  try {
    const saved =
      localStorage.getItem(STORAGE_KEY_SURVIVAL) ??
      localStorage.getItem(STORAGE_KEY_SURVIVAL_LEGACY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<SurvivalChartSettings>;
      const def = createDefaultSettings(t);
      return {
        ...def,
        ...parsed,
        size: { ...def.size, ...parsed.size },
        xAxis: { ...def.xAxis, ...parsed.xAxis },
        yAxis: { ...def.yAxis, ...parsed.yAxis },
        xAxisGrid: { ...def.xAxisGrid, ...parsed.xAxisGrid },
        yAxisGrid: { ...def.yAxisGrid, ...parsed.yAxisGrid },
        textStyles: {
          ...def.textStyles,
          ...parsed.textStyles,
          axisLabel: { ...def.textStyles.axisLabel, ...parsed.textStyles?.axisLabel },
          tickLabel: { ...def.textStyles.tickLabel, ...parsed.textStyles?.tickLabel },
          legendLabel: { ...def.textStyles.legendLabel, ...parsed.textStyles?.legendLabel },
        },
        legend: { ...def.legend, ...parsed.legend },
        groups: { ...def.groups, ...parsed.groups },
        chartBackgroundColor: parsed.chartBackgroundColor ?? def.chartBackgroundColor,
      };
    }
  } catch {
    // ignore
  }
  return createDefaultSettings(t);
}

function saveSurvivalChartSettings(settings: SurvivalChartSettings): void {
  safeLocalStorageSetJson(STORAGE_KEY_SURVIVAL, settings);
}

type SurvivalLegacyChartSettingsContextValue = {
  chartSettings: SurvivalChartSettings;
  setChartSettings: Dispatch<SetStateAction<SurvivalChartSettings>>;
};

const SurvivalLegacyChartSettingsContext = createContext<SurvivalLegacyChartSettingsContextValue | null>(
  null
);

export function KeisukeDashboardSurvivalChartSettingsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [chartSettings, setChartSettings] = useState(() => loadSurvivalSettings(t));
  useEffect(() => {
    saveSurvivalChartSettings(chartSettings);
  }, [chartSettings]);
  return (
    <SurvivalLegacyChartSettingsContext.Provider value={{ chartSettings, setChartSettings }}>
      {children}
    </SurvivalLegacyChartSettingsContext.Provider>
  );
}

function useSurvivalLegacyChartSettings(): SurvivalLegacyChartSettingsContextValue {
  const ctx = useContext(SurvivalLegacyChartSettingsContext);
  if (!ctx) {
    throw new Error('KeisukeDashboardSurvivalChartSettingsProvider が必要です');
  }
  return ctx;
}

export type KeisukeDashboardSurvivalChartLayout = 'split-settings' | 'split-plot';

interface SurvivalSeries {
  group: string;
  points: { time: number; survival: number }[];
  color: string;
  lineWidth: number;
  lineStyle: 'solid' | 'dashed' | 'dotted';
}

interface KeisukeDashboardSurvivalChartProps {
  survivalChart: { group: string; points: { time: number; survival: number }[] }[];
  isCalculating?: boolean;
  /** 表ヘッダー（時間列名）→ X軸ラベル（Yは生存率のため表列名と直結しない） */
  tableTimeColumnLabel?: string;
  layout: KeisukeDashboardSurvivalChartLayout;
}

export default function KeisukeDashboardSurvivalChart({
  survivalChart,
  isCalculating = false,
  tableTimeColumnLabel = '',
  layout,
}: KeisukeDashboardSurvivalChartProps) {
  const { t } = useTranslation();
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const { chartSettings, setChartSettings } = useSurvivalLegacyChartSettings();
  const fid = (suffix: string) => `keisuke-dash-surv-${suffix}`;

  useEffect(() => {
    if (!tableTimeColumnLabel) return;
    setChartSettings((s) => {
      if (s.xAxis.label === tableTimeColumnLabel) return s;
      return { ...s, xAxis: { ...s.xAxis, label: tableTimeColumnLabel } };
    });
  }, [tableTimeColumnLabel, setChartSettings]);

  const handleResetChartSettings = useCallback(() => {
    const def = createDefaultSettings(t);
    setChartSettings({
      ...def,
      xAxis: { ...def.xAxis, label: tableTimeColumnLabel || def.xAxis.label },
    });
  }, [t, setChartSettings, tableTimeColumnLabel]);

  // 群が増えたときに groups にデフォルトを追加
  useEffect(() => {
    if (survivalChart.length === 0) return;
    setChartSettings((prev) => {
      const updated = { ...prev.groups };
      let changed = false;
      survivalChart.forEach((s, i) => {
        if (!updated[s.group]) {
          updated[s.group] = {
            lineColor: DEFAULT_GROUP_COLORS[i % DEFAULT_GROUP_COLORS.length],
            lineWidth: prev.defaultLineWidth,
            lineStyle: 'solid',
          };
          changed = true;
        }
      });
      if (!changed) return prev;
      return { ...prev, groups: updated };
    });
  }, [survivalChart]);

  const seriesWithColors: SurvivalSeries[] = useMemo(
    () =>
      survivalChart.map((s, i) => {
        const g = chartSettings.groups[s.group];
        return {
          group: s.group,
          points: [...s.points].sort((a, b) => a.time - b.time),
          color: g?.lineColor ?? getColor(i),
          lineWidth: g?.lineWidth ?? chartSettings.defaultLineWidth,
          lineStyle: g?.lineStyle ?? 'solid',
        };
      }),
    [survivalChart, chartSettings.groups, chartSettings.defaultLineWidth]
  );

  const xDomain = useMemo((): [number, number] => {
    if (survivalChart.length === 0) return [0, 100];
    let maxTime = 0;
    survivalChart.forEach((s) => {
      s.points.forEach((p) => {
        if (p.time > maxTime) maxTime = p.time;
      });
    });
    return [0, Math.max(maxTime * 1.05, 1)];
  }, [survivalChart]);

  const yDomain: [number, number] = [0, 100];

  const chartMarginForChart = useMemo(() => ({ ...DEFAULT_MARGIN }), []);

  const chartWidth = useMemo(() => {
    if (chartSettings.size.plotAreaWidth != null) {
      return (
        chartSettings.size.plotAreaWidth + chartMarginForChart.left + chartMarginForChart.right
      );
    }
    return chartSettings.size.width ?? undefined;
  }, [chartSettings.size.plotAreaWidth, chartSettings.size.width, chartMarginForChart]);

  /** 右ペイン（split-plot）では親幅に合わせ、保存された固定幅は使わない */
  const chartWidthForKeisuke = layout === 'split-plot' ? undefined : chartWidth;

  const chartHeight = useMemo(() => {
    if (chartSettings.size.plotAreaHeight != null) {
      return (
        chartSettings.size.plotAreaHeight + chartMarginForChart.top + chartMarginForChart.bottom
      );
    }
    return chartSettings.size.height ?? DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX;
  }, [chartSettings.size.plotAreaHeight, chartSettings.size.height, chartMarginForChart]);

  const getExportBaseName = () => `keisuke_survival_${new Date().toISOString().slice(0, 10)}`;

  const renderSvgToCanvas = useCallback((callback: (canvas: HTMLCanvasElement) => void) => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      callback(canvas);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);

  const handleExportSvg = useCallback(() => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getExportBaseName()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleCopySvgToClipboard = useCallback(async () => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    await copySvgElementAsPngToClipboard(svg);
  }, []);

  const handleExportPng = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${getExportBaseName()}.png`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportJpeg = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg', 0.92);
      a.download = `${getExportBaseName()}.jpg`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportTiff = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const baseName = getExportBaseName();
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width;
        const h = canvas.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const tiffBuffer = UTIF.encodeImage(imageData.data, w, h);
        const blob = new Blob([tiffBuffer], { type: 'image/tiff' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}.tiff`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${baseName}.png`;
        a.click();
      }
    });
  }, [renderSvgToCanvas]);

  const noPlotData =
    survivalChart.length === 0 || seriesWithColors.every((s) => s.points.length === 0);

  if (layout === 'split-plot' && noPlotData) {
    return (
      <div className={styles.chartWrap} style={{ opacity: isCalculating ? 0.6 : 1 }}>
        <div className={`${styles.chartRootWrapper} ${styles.chartRoot}`} style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted small">{t('keisuke.dashboard.chart.noData')}</span>
        </div>
      </div>
    );
  }

  const textStyle = {
    fontSize: chartSettings.textStyles.tickLabel.fontSize,
    fontWeight: 'normal' as const,
    fontStyle: 'normal' as const,
    fontFamily: 'Arial, sans-serif',
    fill: chartSettings.textStyles.tickLabel.color,
  };
  const axisLabelStyle = {
    ...textStyle,
    fontSize: chartSettings.textStyles.axisLabel.fontSize,
    fontWeight: '500' as const,
    fill: chartSettings.textStyles.axisLabel.color,
  };

  return (
    <div
      ref={chartWrapRef}
      className={styles.chartWrap}
      style={{
        opacity: isCalculating ? 0.6 : 1,
        pointerEvents: isCalculating ? 'none' : 'auto',
        flex: '1 1 0',
        minHeight: 0,
        overflowX: layout === 'split-plot' ? 'auto' : 'hidden',
        overflowY: layout === 'split-plot' ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {layout === 'split-settings' ? (
        <div className={styles.chartSettingsSplitRoot}>
      <Accordion className={`mb-2 ${styles.chartSettingsAccordion}`} defaultActiveKey="chart-settings">
        <Accordion.Item eventKey="chart-settings">
          <Accordion.Header className="py-1 small">{t('keisuke.dashboard.chart.settings')}</Accordion.Header>
          <Accordion.Body className="py-2 small">
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                type="button"
                className={styles.chartSettingsIconBtn}
                onClick={handleResetChartSettings}
                title={t('keisuke.dashboard.chart.resetToDefaultsHint')}
                aria-label={t('keisuke.dashboard.chart.resetToDefaultsHint')}
              >
                <i className="fas fa-undo-alt" aria-hidden />
              </Button>
            </div>
            <div className={`row g-3 ${styles.chartSettingsStack}`}>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.dashboard.chart.graphSettingsSection')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('chart-width')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.width')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-width')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.width ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, width: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-height')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.height')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-height')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.height ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, height: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-plotAreaWidth')}>
                    <Form.Label className="small">{t('keisuke.anova.plotAreaWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-plotAreaWidth')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.plotAreaWidth ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, plotAreaWidth: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('chart-plotAreaHeight')}>
                    <Form.Label className="small">{t('keisuke.anova.plotAreaHeight')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('chart-plotAreaHeight')} min={200} step={20} placeholder={t('keisuke.dashboard.chart.auto')} value={chartSettings.size.plotAreaHeight ?? ''} onChange={(e) => setChartSettings((s) => ({ ...s, size: { ...s.size, plotAreaHeight: e.target.value ? parseInt(e.target.value, 10) : null } }))} />
                  </Form.Group>
                  <Form.Group
                    className={styles.chartSizeBackgroundGroup}
                    role="group"
                    aria-label={t('keisuke.dashboard.chart.backgroundColor')}
                  >
                    <div className="small fw-medium mb-1">{t('keisuke.dashboard.chart.backgroundColor')}</div>
                    <Form.Check
                      type="checkbox"
                      className="mb-1"
                      id={fid('chart-bg-transparent')}
                      name={fid('chart-bg-transparent')}
                      label={t('keisuke.dashboard.chart.backgroundColorTransparent')}
                      checked={chartSettings.chartBackgroundColor === 'transparent'}
                      onChange={(e) => setChartSettings((s) => ({ ...s, chartBackgroundColor: e.target.checked ? 'transparent' : '#ffffff' }))}
                    />
                    {chartSettings.chartBackgroundColor !== 'transparent' && (
                      <Form.Control
                        id={fid('chart-bg-color')}
                        type="color"
                        size="sm"
                        name={fid('chart-bg-color')}
                        aria-label={t('keisuke.dashboard.chart.backgroundColor')}
                        value={chartSettings.chartBackgroundColor}
                        onChange={(e) => setChartSettings((s) => ({ ...s, chartBackgroundColor: e.target.value }))}
                        style={{ width: '100%', maxWidth: 120 }}
                      />
                    )}
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.survival.xAxisSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('xAxis-label')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLabel')}</Form.Label>
                    <Form.Control type="text" size="sm" name={fid('xAxis-label')} value={chartSettings.xAxis.label} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, label: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-lineWidth')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLineWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('xAxis-lineWidth')} min={0.5} max={10} step={0.5} value={chartSettings.xAxis.lineWidth} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, lineWidth: parseFloat(e.target.value) || 3 } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxis-labelPosition')}>
                    <Form.Label className="small">{t('keisuke.survival.xAxisLabelPosition')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('xAxis-labelPosition')} step={5} value={chartSettings.xAxis.labelPosition} onChange={(e) => setChartSettings((s) => ({ ...s, xAxis: { ...s.xAxis, labelPosition: parseFloat(e.target.value) || 40 } }))} />
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.survival.xAxisGridSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('xAxisGrid-show')}>
                    <Form.Check type="checkbox" id={fid('xAxisGrid-show')} name={fid('xAxisGrid-show')} label={t('keisuke.survival.showGrid')} checked={chartSettings.xAxisGrid.show} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, show: e.target.checked } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxisGrid-lineStyle')}>
                    <Form.Label className="small">{t('keisuke.survival.gridLineStyle')}</Form.Label>
                    <Form.Select id={fid('xAxisGrid-lineStyle')} name={fid('xAxisGrid-lineStyle')} size="sm" value={chartSettings.xAxisGrid.lineStyle} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } }))}>
                      <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                      <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                      <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className={styles.chartSettingsFieldRange} controlId={fid('xAxisGrid-opacity')}>
                    <Form.Label className="small" htmlFor={fid('xAxisGrid-opacity-range')}>
                      {t('keisuke.survival.gridOpacity')}
                    </Form.Label>
                    <Form.Range id={fid('xAxisGrid-opacity-range')} name={fid('xAxisGrid-opacity')} min={0} max={1} step={0.1} value={chartSettings.xAxisGrid.opacity} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, opacity: parseFloat(e.target.value) } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('xAxisGrid-color')}>
                    <Form.Label className="small">{t('keisuke.survival.gridColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('xAxisGrid-color')} value={chartSettings.xAxisGrid.color} onChange={(e) => setChartSettings((s) => ({ ...s, xAxisGrid: { ...s.xAxisGrid, color: e.target.value } }))} />
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.survival.yAxisSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('yAxis-label')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLabel')}</Form.Label>
                    <Form.Control type="text" size="sm" name={fid('yAxis-label')} value={chartSettings.yAxis.label} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, label: e.target.value } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-lineWidth')}>
                    <Form.Label className="small">{t('keisuke.survival.axisLineWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('yAxis-lineWidth')} min={0.5} max={10} step={0.5} value={chartSettings.yAxis.lineWidth} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, lineWidth: parseFloat(e.target.value) || 3 } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxis-labelPosition')}>
                    <Form.Label className="small">{t('keisuke.survival.yAxisLabelPosition')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('yAxis-labelPosition')} step={5} value={chartSettings.yAxis.labelPosition} onChange={(e) => setChartSettings((s) => ({ ...s, yAxis: { ...s.yAxis, labelPosition: parseFloat(e.target.value) || -65 } }))} />
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.survival.yAxisGridSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('yAxisGrid-show')}>
                    <Form.Check type="checkbox" id={fid('yAxisGrid-show')} name={fid('yAxisGrid-show')} label={t('keisuke.survival.showGrid')} checked={chartSettings.yAxisGrid.show} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, show: e.target.checked } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxisGrid-lineStyle')}>
                    <Form.Label className="small">{t('keisuke.survival.gridLineStyle')}</Form.Label>
                    <Form.Select id={fid('yAxisGrid-lineStyle')} name={fid('yAxisGrid-lineStyle')} size="sm" value={chartSettings.yAxisGrid.lineStyle} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } }))}>
                      <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                      <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                      <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className={styles.chartSettingsFieldRange} controlId={fid('yAxisGrid-opacity')}>
                    <Form.Label className="small" htmlFor={fid('yAxisGrid-opacity-range')}>
                      {t('keisuke.survival.gridOpacity')}
                    </Form.Label>
                    <Form.Range id={fid('yAxisGrid-opacity-range')} name={fid('yAxisGrid-opacity')} min={0} max={1} step={0.1} value={chartSettings.yAxisGrid.opacity} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, opacity: parseFloat(e.target.value) } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('yAxisGrid-color')}>
                    <Form.Label className="small">{t('keisuke.survival.gridColor')}</Form.Label>
                    <Form.Control type="color" size="sm" name={fid('yAxisGrid-color')} value={chartSettings.yAxisGrid.color} onChange={(e) => setChartSettings((s) => ({ ...s, yAxisGrid: { ...s.yAxisGrid, color: e.target.value } }))} />
                  </Form.Group>
                </div>
              </div>
              </div>
              <div className="col-12">
                <div className={styles.chartSettingsColumn}>
                <h6 className="mb-2 small">{t('keisuke.anova.plotSettings')}</h6>
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('defaultLineWidth')}>
                    <Form.Label className="small">{t('keisuke.survival.defaultLineWidth')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('defaultLineWidth')} min={1} max={6} value={chartSettings.defaultLineWidth} onChange={(e) => setChartSettings((s) => ({ ...s, defaultLineWidth: parseInt(e.target.value, 10) || 2 }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('legend-show')}>
                    <Form.Check type="checkbox" id={fid('legend-show')} name={fid('legend-show')} label={t('keisuke.dashboard.chart.showLegend')} checked={chartSettings.legend.show} onChange={(e) => setChartSettings((s) => ({ ...s, legend: { ...s.legend, show: e.target.checked } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('legend-position')}>
                    <Form.Label className="small">{t('keisuke.anova.legendPosition')}</Form.Label>
                    <Form.Select id={fid('legend-position')} name={fid('legend-position')} size="sm" value={chartSettings.legend.position} onChange={(e) => setChartSettings((s) => ({ ...s, legend: { ...s.legend, position: e.target.value as 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' } }))}>
                      <option value="top-right">{t('keisuke.survival.legendPositionTopRight')}</option>
                      <option value="top-left">{t('keisuke.survival.legendPositionTopLeft')}</option>
                      <option value="bottom-right">{t('keisuke.survival.legendPositionBottomRight')}</option>
                      <option value="bottom-left">{t('keisuke.survival.legendPositionBottomLeft')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <h6 className="mt-2 mb-2 small">{t('keisuke.survival.groupLineSettings')}</h6>
                {seriesWithColors.map((groupData, groupIdx) => (
                  <div key={groupData.group} className="mb-2 p-2 border rounded">
                    <span className="small fw-bold d-block mb-1">{groupData.group}</span>
                    <div className={styles.chartSettingsFieldsRow}>
                      <Form.Group controlId={fid(`grp-${groupIdx}-lineColor`)}>
                        <Form.Label className="small">{t('keisuke.survival.lineColor')}</Form.Label>
                        <Form.Control type="color" size="sm" name={fid(`grp-${groupIdx}-lineColor`)} value={chartSettings.groups[groupData.group]?.lineColor ?? groupData.color} onChange={(e) => setChartSettings((s) => ({ ...s, groups: { ...s.groups, [groupData.group]: { ...(s.groups[groupData.group] ?? { lineColor: groupData.color, lineWidth: s.defaultLineWidth, lineStyle: 'solid' }), lineColor: e.target.value } } }))} />
                      </Form.Group>
                      <Form.Group controlId={fid(`grp-${groupIdx}-lineWidth`)}>
                        <Form.Label className="small">{t('keisuke.survival.lineWidth')}</Form.Label>
                        <Form.Control type="number" size="sm" name={fid(`grp-${groupIdx}-lineWidth`)} min={1} max={6} value={chartSettings.groups[groupData.group]?.lineWidth ?? chartSettings.defaultLineWidth} onChange={(e) => setChartSettings((s) => ({ ...s, groups: { ...s.groups, [groupData.group]: { ...(s.groups[groupData.group] ?? { lineColor: groupData.color, lineWidth: s.defaultLineWidth, lineStyle: 'solid' }), lineWidth: parseInt(e.target.value, 10) || 2 } } }))} />
                      </Form.Group>
                      <Form.Group controlId={fid(`grp-${groupIdx}-lineStyle`)}>
                        <Form.Label className="small">{t('keisuke.survival.lineStyle')}</Form.Label>
                        <Form.Select id={fid(`grp-${groupIdx}-lineStyle`)} name={fid(`grp-${groupIdx}-lineStyle`)} size="sm" value={chartSettings.groups[groupData.group]?.lineStyle ?? 'solid'} onChange={(e) => setChartSettings((s) => ({ ...s, groups: { ...s.groups, [groupData.group]: { ...(s.groups[groupData.group] ?? { lineColor: groupData.color, lineWidth: s.defaultLineWidth, lineStyle: 'solid' }), lineStyle: e.target.value as 'solid' | 'dashed' | 'dotted' } } }))}>
                          <option value="solid">{t('keisuke.dashboard.chart.gridLineSolid')}</option>
                          <option value="dashed">{t('keisuke.dashboard.chart.gridLineDashed')}</option>
                          <option value="dotted">{t('keisuke.dashboard.chart.gridLineDotted')}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>
                ))}
                <div className={styles.chartSettingsFieldsRow}>
                  <Form.Group controlId={fid('text-axisLabel-fontSize')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.axisLabelFontSize')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('text-axisLabel-fontSize')} min={8} max={24} value={chartSettings.textStyles.axisLabel.fontSize} onChange={(e) => setChartSettings((s) => ({ ...s, textStyles: { ...s.textStyles, axisLabel: { ...s.textStyles.axisLabel, fontSize: parseInt(e.target.value, 10) || 16 } } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('text-axisLabel-color')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.axisLabelColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      size="sm"
                      name={fid('text-axisLabel-color')}
                      value={chartSettings.textStyles.axisLabel.color}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: { ...s.textStyles, axisLabel: { ...s.textStyles.axisLabel, color: e.target.value } },
                        }))
                      }
                      style={{ width: '100%', maxWidth: 120 }}
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('text-tickLabel-fontSize')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.tickLabelFontSize')}</Form.Label>
                    <Form.Control type="number" size="sm" name={fid('text-tickLabel-fontSize')} min={8} max={20} value={chartSettings.textStyles.tickLabel.fontSize} onChange={(e) => setChartSettings((s) => ({ ...s, textStyles: { ...s.textStyles, tickLabel: { ...s.textStyles.tickLabel, fontSize: parseInt(e.target.value, 10) || 16 } } }))} />
                  </Form.Group>
                  <Form.Group controlId={fid('text-tickLabel-color')}>
                    <Form.Label className="small">{t('keisuke.dashboard.chart.tickLabelColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      size="sm"
                      name={fid('text-tickLabel-color')}
                      value={chartSettings.textStyles.tickLabel.color}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: { ...s.textStyles, tickLabel: { ...s.textStyles.tickLabel, color: e.target.value } },
                        }))
                      }
                      style={{ width: '100%', maxWidth: 120 }}
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('text-legendLabel-fontSize')}>
                    <Form.Label className="small">{t('keisuke.survival.legendFontSize')}</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      name={fid('text-legendLabel-fontSize')}
                      min={8}
                      max={20}
                      value={chartSettings.textStyles.legendLabel.fontSize}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: {
                            ...s.textStyles,
                            legendLabel: {
                              ...s.textStyles.legendLabel,
                              fontSize: parseInt(e.target.value, 10) || 12,
                            },
                          },
                        }))
                      }
                    />
                  </Form.Group>
                  <Form.Group controlId={fid('text-legendLabel-color')}>
                    <Form.Label className="small">{t('keisuke.survival.legendColor')}</Form.Label>
                    <Form.Control
                      type="color"
                      size="sm"
                      name={fid('text-legendLabel-color')}
                      value={chartSettings.textStyles.legendLabel.color}
                      onChange={(e) =>
                        setChartSettings((s) => ({
                          ...s,
                          textStyles: {
                            ...s.textStyles,
                            legendLabel: { ...s.textStyles.legendLabel, color: e.target.value },
                          },
                        }))
                      }
                      style={{ width: '100%', maxWidth: 120 }}
                    />
                  </Form.Group>
                </div>
              </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
        </div>
      ) : (
      <div
        style={{
          flex: '1 1 0',
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div ref={chartContainerRef} className={styles.chartRootWrapper}>
        <KeisukeDashboardChartExportDropdown
          onCopyClipboard={handleCopySvgToClipboard}
          onExportPng={handleExportPng}
          onExportSvg={handleExportSvg}
          onExportTiff={handleExportTiff}
          onExportJpeg={handleExportJpeg}
        />
        <KeisukeChart
          height={chartHeight}
          width={chartWidthForKeisuke}
          margin={chartMarginForChart}
          backgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
          plotAreaBackgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
        >
          {(dimensions: ChartDimensions) => {
            const plotAreaBounds = {
              x: dimensions.margin.left,
              y: dimensions.margin.top,
              width: dimensions.width - dimensions.margin.left - dimensions.margin.right,
              height: dimensions.height - dimensions.margin.top - dimensions.margin.bottom,
            };
            const xScale = createLinearScale(xDomain, [0, plotAreaBounds.width]);
            const yScale = createLinearScale(yDomain, [0, plotAreaBounds.height]);
            const yScaleReversed = (v: number) => plotAreaBounds.height - yScale(v);
            const xAxisScale: AxisScale = { domain: xDomain, range: [0, plotAreaBounds.width], ticks: calculateNiceTicks(xDomain, 5) };
            const yAxisScale: AxisScale = { domain: yDomain, range: [0, plotAreaBounds.height], ticks: calculateNiceTicks(yDomain, 5) };

            const lineDash = (style: 'solid' | 'dashed' | 'dotted') => (style === 'dashed' ? '8,4' : style === 'dotted' ? '2,2' : undefined);
            const legendPos = chartSettings.legend.position;
            const legendX = legendPos === 'top-right' || legendPos === 'bottom-right' ? plotAreaBounds.width - 120 : 0;
            const legendY = legendPos === 'top-right' || legendPos === 'top-left' ? 10 : plotAreaBounds.height - 10 - seriesWithColors.length * 22;
            return (
              <>
                <KeisukeAxis bounds={plotAreaBounds} dimensions={dimensions} type="x" scale={xAxisScale} label={chartSettings.xAxis.label} showGrid={chartSettings.xAxisGrid.show} gridLineStyle={chartSettings.xAxisGrid.lineStyle} gridOpacity={chartSettings.xAxisGrid.opacity} gridColor={chartSettings.xAxisGrid.color} axisLineWidth={chartSettings.xAxis.lineWidth} xAxisLabelPosition={chartSettings.xAxis.labelPosition} axisLabelStyle={axisLabelStyle} tickLabelStyle={textStyle} />
                <KeisukeAxis bounds={plotAreaBounds} dimensions={dimensions} type="y" scale={yAxisScale} label={chartSettings.yAxis.label} showGrid={chartSettings.yAxisGrid.show} gridLineStyle={chartSettings.yAxisGrid.lineStyle} gridOpacity={chartSettings.yAxisGrid.opacity} gridColor={chartSettings.yAxisGrid.color} axisLineWidth={chartSettings.yAxis.lineWidth} yAxisLabelPosition={chartSettings.yAxis.labelPosition} axisLabelStyle={axisLabelStyle} tickLabelStyle={textStyle} />
                <g transform={`translate(${plotAreaBounds.x}, ${plotAreaBounds.y})`}>
                  {seriesWithColors.map((groupData, groupIndex) => {
                    const curve = groupData.points;
                    if (curve.length === 0) return null;
                    const pathData: string[] = [];
                    pathData.push(`M ${xScale(0)},${yScaleReversed(100)}`);
                    for (let i = 0; i < curve.length; i++) {
                      const point = curve[i];
                      const x = xScale(point.time);
                      const y = yScaleReversed(point.survival);
                      if (i === 0) {
                        pathData.push(`L ${x},${yScaleReversed(100)}`);
                        pathData.push(`L ${x},${y}`);
                      } else {
                        const prev = curve[i - 1];
                        pathData.push(`L ${x},${yScaleReversed(prev.survival)}`);
                        pathData.push(`L ${x},${y}`);
                      }
                    }
                    const last = curve[curve.length - 1];
                    pathData.push(`L ${xScale(xDomain[1])},${yScaleReversed(last.survival)}`);
                    return (
                      <path
                        key={groupIndex}
                        d={pathData.join(' ')}
                        fill="none"
                        stroke={groupData.color}
                        strokeWidth={groupData.lineWidth}
                        strokeDasharray={lineDash(groupData.lineStyle)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    );
                  })}
                  {chartSettings.legend.show && (
                    <g transform={`translate(${legendX}, ${legendY})`}>
                      {seriesWithColors.map((groupData, index) => (
                        <g key={index} transform={`translate(0, ${index * 22})`}>
                          <line x1="0" y1="10" x2="18" y2="10" stroke={groupData.color} strokeWidth={groupData.lineWidth} strokeDasharray={lineDash(groupData.lineStyle)} />
                          <text
                            x="24"
                            y="14"
                            fontSize={chartSettings.textStyles.legendLabel.fontSize}
                            fontFamily="Arial, sans-serif"
                            fill={chartSettings.textStyles.legendLabel.color}
                          >
                            {groupData.group}
                          </text>
                        </g>
                      ))}
                    </g>
                  )}
                </g>
              </>
            );
          }}
        </KeisukeChart>
      </div>
      </div>
      )}
    </div>
  );
}
